import _ from 'lodash';
import type { WidgetVersionConfig, WidgetVersionConfigRule } from '@dopomogai/types';
import type { WidgetConfig } from '../types/widget';
import { defaultWidgetConfig } from './widgetDefaults';

/**
 * Produces a fresh deep copy of the UI widget config defaults.
 */
export const createBaseWidgetConfig = (): WidgetConfig => _.cloneDeep(defaultWidgetConfig);

/**
 * Maps a backend WidgetVersionConfig into the UI WidgetConfig.
 */
export const apiToUiConfig = (apiConfig?: WidgetVersionConfig | null): WidgetConfig => {
  const base = createBaseWidgetConfig();
  if (!apiConfig) return base;

  const welcomeStrategyCandidates: WidgetConfig['welcomeStrategy'][] = ['default', 'rules', 'llm'];
    const welcomeStrategy = welcomeStrategyCandidates.includes(
    apiConfig.welcomeStrategy as WidgetConfig['welcomeStrategy']
      )
        ? (apiConfig.welcomeStrategy as WidgetConfig['welcomeStrategy'])
        : base.welcomeStrategy;  
    
  const rules = Array.isArray(apiConfig.ruleBasedWelcomeMessages)
    ? apiConfig.ruleBasedWelcomeMessages
    : [];

  const showThinkingProcessFromApi =
    (apiConfig as any).showThinkingProcessIndicator ??
    (apiConfig as any).showThinkingProcess ??
    base.showThinkingProcess;

  // Determine context settings
  const cpObj = (apiConfig as any).contextPrepending || {};
  const cpEnabled = (apiConfig as any).contextPrependingEnabled;
  const contextUrl =
    typeof cpObj.url === 'boolean'
      ? cpObj.url
      : typeof cpEnabled === 'boolean'
      ? cpEnabled
      : base.contextPrepending.url;
  const contextHostname =
    typeof cpObj.hostname === 'boolean' ? cpObj.hostname : base.contextPrepending.hostname;
  const contextStore =
    typeof cpObj.store === 'boolean' ? cpObj.store : base.contextPrepending.store;

  // Device-aware defaults and mobile modes
  const defaultMinimizedFallback = (apiConfig as any).defaultMinimized;
  const defaultMinimizedDesktop =
    (apiConfig as any).defaultMinimizedDesktop ??
    (typeof defaultMinimizedFallback === 'boolean' ? defaultMinimizedFallback : base.defaultMinimizedDesktop);
  const defaultMinimizedMobile =
    (apiConfig as any).defaultMinimizedMobile ??
    (typeof defaultMinimizedFallback === 'boolean' ? defaultMinimizedFallback : base.defaultMinimizedMobile);
  const autoOpen = {
    delayDesktop:
      typeof (apiConfig as any).autoOpen?.delayDesktop === 'number'
        ? (apiConfig as any).autoOpen.delayDesktop
        : typeof (apiConfig as any).autoOpenDelay === 'number'
        ? (apiConfig as any).autoOpenDelay
        : base.autoOpen.delayDesktop,
    delayMobile:
      typeof (apiConfig as any).autoOpen?.delayMobile === 'number'
        ? (apiConfig as any).autoOpen.delayMobile
        : typeof (apiConfig as any).autoOpenDelay === 'number'
        ? (apiConfig as any).autoOpenDelay
        : base.autoOpen.delayMobile,
    oncePerSession:
      typeof (apiConfig as any).autoOpen?.oncePerSession === 'boolean'
        ? (apiConfig as any).autoOpen.oncePerSession
        : base.autoOpen.oncePerSession,
  };
  const mobileMode = (apiConfig as any).mobileMode ?? base.mobileMode;
  const mobileHalfHeight = (apiConfig as any).mobileHalfHeight ?? base.mobileHalfHeight;

  // Merge appearance settings if present
  const appearance = { ...base.appearance };
  if (apiConfig.appearance && typeof apiConfig.appearance === 'object') {
    const ap = apiConfig.appearance;
    if (typeof ap.fontFamily === 'string') appearance.fontFamily = ap.fontFamily;
    if (typeof ap.fontSizeBase === 'string') appearance.fontSizeBase = ap.fontSizeBase;
    if (typeof ap.widgetShadow === 'string') appearance.widgetShadow = ap.widgetShadow;
    if (typeof ap.bubbleShadow === 'string') appearance.bubbleShadow = ap.bubbleShadow;
    if (typeof ap.widgetMaxWidth === 'string') appearance.widgetMaxWidth = ap.widgetMaxWidth;
    if (typeof ap.widgetMaxHeight === 'string') appearance.widgetMaxHeight = ap.widgetMaxHeight;
    if (typeof ap.showCollapsedThinking === 'boolean') {
      appearance.showCollapsedThinking = ap.showCollapsedThinking;
    }
    if (typeof ap.maxMessageLength === 'number') {
      appearance.maxMessageLength = ap.maxMessageLength;
    }
    if (typeof ap.defaultMinimized === 'boolean') {
      appearance.defaultMinimized = ap.defaultMinimized;
    }
    if (typeof ap.autoOpenDelay === 'number') {
      appearance.autoOpenDelay = ap.autoOpenDelay;
    }
  }
  // Fallback to legacy flattened appearance keys
  if (typeof (apiConfig as any).fontFamily === 'string') appearance.fontFamily = (apiConfig as any).fontFamily;
  if (typeof (apiConfig as any).fontSizeBase === 'string') appearance.fontSizeBase = (apiConfig as any).fontSizeBase;
  if (typeof (apiConfig as any).widgetShadow === 'string') appearance.widgetShadow = (apiConfig as any).widgetShadow;
  if (typeof (apiConfig as any).bubbleShadow === 'string') appearance.bubbleShadow = (apiConfig as any).bubbleShadow;
  if (typeof (apiConfig as any).widgetMaxWidth === 'string') appearance.widgetMaxWidth = (apiConfig as any).widgetMaxWidth;
  if (typeof (apiConfig as any).widgetMaxHeight === 'string') appearance.widgetMaxHeight = (apiConfig as any).widgetMaxHeight;
  if (typeof (apiConfig as any).defaultMinimized === 'boolean') {
    appearance.defaultMinimized = (apiConfig as any).defaultMinimized;
  }
  if (typeof (apiConfig as any).autoOpenDelay === 'number') {
    appearance.autoOpenDelay = (apiConfig as any).autoOpenDelay;
  }
  if (typeof (apiConfig as any).showCollapsedThinking === 'boolean') {
    appearance.showCollapsedThinking = (apiConfig as any).showCollapsedThinking;
  }
  if (typeof (apiConfig as any).maxMessageLength === 'number') {
    appearance.maxMessageLength = (apiConfig as any).maxMessageLength;
  }

  // Colors merged (including extended palette)
  const colors = { ...base.colors };
  if (apiConfig.colors && typeof apiConfig.colors === 'object') {
    const col = apiConfig.colors;
    if (typeof col.primary === 'string') colors.primary = col.primary;
    if (typeof col.primaryGradient === 'string') colors.primaryGradient = col.primaryGradient;
    if (typeof col.background === 'string') colors.widgetBackground = col.background;
    if (typeof col.messagesBackground === 'string') {
      colors.messagesBackground = col.messagesBackground;
    }
    if (typeof col.textOnPrimary === 'string') colors.textColorOnPrimary = col.textOnPrimary;
    if (typeof col.textOnBackground === 'string') colors.textColorPrimary = col.textOnBackground;
    if (typeof col.textColorSecondary === 'string') {
      colors.textColorSecondary = col.textColorSecondary;
    }
    if (typeof col.userBubbleBackground === 'string') {
      colors.userBubbleBackground = col.userBubbleBackground;
    }
    if (typeof col.textColorUserBubble === 'string') {
      colors.textColorUserBubble = col.textColorUserBubble;
    }
    if (typeof col.botBubbleBackground === 'string') {
      colors.botBubbleBackground = col.botBubbleBackground;
    }
    if (typeof col.textColorBotBubble === 'string') {
      colors.textColorBotBubble = col.textColorBotBubble;
    }
    if (typeof col.botBubbleBorder === 'string') colors.botBubbleBorder = col.botBubbleBorder;
    if (typeof col.systemBubbleBackground === 'string') {
      colors.systemBubbleBackground = col.systemBubbleBackground;
    }
    if (typeof col.systemBubbleColor === 'string') {
      colors.systemBubbleColor = col.systemBubbleColor;
    }
    if (typeof col.systemBubbleBorder === 'string') {
      colors.systemBubbleBorder = col.systemBubbleBorder;
    }
    if (typeof col.borderColor === 'string') colors.borderColor = col.borderColor;
    if (typeof col.focusRingColor === 'string') colors.focusRingColor = col.focusRingColor;
  }
  // Theme/legacy color fallbacks
  if (typeof (apiConfig as any).primaryColor === 'string') {
    colors.primary = (apiConfig as any).primaryColor;
  }
  if (typeof (apiConfig as any).themeColor === 'string' && colors.primary === base.colors.primary) {
    colors.primary = (apiConfig as any).themeColor;
  }
  if (typeof (apiConfig as any).widgetBackground === 'string') {
    colors.widgetBackground = (apiConfig as any).widgetBackground;
  }
  if (typeof (apiConfig as any).textColorPrimary === 'string') {
    colors.textColorPrimary = (apiConfig as any).textColorPrimary;
  }
  if (typeof (apiConfig as any).textColorOnPrimary === 'string') {
    colors.textColorOnPrimary = (apiConfig as any).textColorOnPrimary;
  }

  const markdownSupport = {
    enableUserMarkdown:
      (apiConfig as any).markdownSupport?.enableUserMarkdown ??
      base.markdownSupport.enableUserMarkdown,
    enableAssistantMarkdown:
      (apiConfig as any).markdownSupport?.enableAssistantMarkdown ??
      base.markdownSupport.enableAssistantMarkdown,
  };

  const errorMessages = {
    default:
      (apiConfig as any).errorMessages?.default ?? base.errorMessages.default,
    unauthorized:
      (apiConfig as any).errorMessages?.unauthorized ?? base.errorMessages.unauthorized,
  };

  const featureObj = (apiConfig as any).features || {};
  const features = {
    threadsEnabled:
      typeof featureObj.threadsEnabled === 'boolean'
        ? featureObj.threadsEnabled
        : base.features.threadsEnabled,
    commentsEnabled:
      typeof featureObj.commentsEnabled === 'boolean'
        ? featureObj.commentsEnabled
        : base.features.commentsEnabled,
    attachmentsEnabled:
      typeof featureObj.attachmentsEnabled === 'boolean'
        ? featureObj.attachmentsEnabled
        : base.features.attachmentsEnabled,
    historyEnabled:
      typeof featureObj.historyEnabled === 'boolean'
        ? featureObj.historyEnabled
        : base.features.historyEnabled,
    tabsEnabled:
      typeof featureObj.tabsEnabled === 'boolean'
        ? featureObj.tabsEnabled
        : base.features.tabsEnabled,
  };

  return {
    ...base,
    preset: (apiConfig as any).preset ?? base.preset,
    widgetTitle: apiConfig.widgetTitle ?? base.widgetTitle,
    collapsedMessage: apiConfig.collapsedMessage ?? base.collapsedMessage,
    inputPlaceholder: (apiConfig as any).inputPlaceholder ?? apiConfig.placeholderWelcome ?? base.inputPlaceholder,
    botAvatarImageUrl:
      (apiConfig as any).botAvatarImageUrl ??
      (apiConfig as any).botAvatarImage ??
      base.botAvatarImageUrl,
    welcomeStrategy,
    defaultWelcome:
      (apiConfig as any).defaultWelcome ??
      (apiConfig as any).defaultWelcomeMessage ??
      (apiConfig as any).welcomeMessage ??
      base.defaultWelcome,
    ruleBasedWelcome: rules.map((rule: any) => ({
      if_url_contains: rule.condition_type === 'url_contains' ? rule.condition_value : '',
      if_hostname_is: '',
      message: rule.welcome_message ?? '',
    })),
    llmWelcomePrompt: apiConfig.llmWelcomePrompt ?? base.llmWelcomePrompt,
    llmWelcomeModel: apiConfig.llmWelcomeModelId ?? base.llmWelcomeModel,

    showThinkingProcess: showThinkingProcessFromApi,
    streamingEnabled: apiConfig.streamingEnabled ?? base.streamingEnabled,

    defaultMinimizedDesktop,
    defaultMinimizedMobile,
    autoOpen,
    mobileMode,
    mobileHalfHeight,

    contextPrepending: {
      url: contextUrl,
      hostname: contextHostname,
      store: contextStore,
    },

    colors,
    features,
    appearance,
    markdownSupport,
    errorMessages,
  };
};

/**
 * Maps the UI WidgetConfig into a backend WidgetVersionConfig (partial).
 */
export const uiToApiConfig = (uiConfig: WidgetConfig): WidgetVersionConfig => {
  const rules =
    uiConfig.ruleBasedWelcome?.map((rule, index) => ({
      id: `rule-${index}`,
      condition_type: 'url_contains',
      condition_value: rule.if_url_contains,
      welcome_message: rule.message,
      priority: index,
    })) ?? [];

  return {
    preset: uiConfig.preset,
    widgetTitle: uiConfig.widgetTitle,
    collapsedMessage: uiConfig.collapsedMessage,
    placeholderWelcome: uiConfig.inputPlaceholder,
    inputPlaceholder: uiConfig.inputPlaceholder,
    botAvatarImageUrl: uiConfig.botAvatarImageUrl,
    botAvatarImage: uiConfig.botAvatarImageUrl,
    welcomeStrategy: uiConfig.welcomeStrategy,
    defaultWelcomeMessage: uiConfig.defaultWelcome,
    welcomeMessage: uiConfig.defaultWelcome,
        ruleBasedWelcomeMessages: rules,
    llmWelcomePrompt: uiConfig.llmWelcomePrompt,
    llmWelcomeModelId: uiConfig.llmWelcomeModel,
    themeColor: uiConfig.themeColor || uiConfig.colors.primary,
    position: uiConfig.position,

    contextPrependingEnabled: uiConfig.contextPrepending?.url ?? false,
    contextPrepending: {
      url: !!uiConfig.contextPrepending?.url,
      hostname: !!uiConfig.contextPrepending?.hostname,
      store: !!uiConfig.contextPrepending?.store,
    },

    showThinkingProcessIndicator: uiConfig.showThinkingProcess,
    showThinkingProcess: uiConfig.showThinkingProcess,
    streamingEnabled: uiConfig.streamingEnabled,
    features: {
      threadsEnabled: uiConfig.features.threadsEnabled,
      commentsEnabled: uiConfig.features.commentsEnabled,
      attachmentsEnabled: uiConfig.features.attachmentsEnabled,
      historyEnabled: uiConfig.features.historyEnabled,
      tabsEnabled: uiConfig.features.tabsEnabled,
    },

    colors: {
      primary: uiConfig.colors.primary,
      primaryGradient: uiConfig.colors.primaryGradient,
      background: uiConfig.colors.widgetBackground,
      messagesBackground: uiConfig.colors.messagesBackground,
      textOnPrimary: uiConfig.colors.textColorOnPrimary,
      textOnBackground: uiConfig.colors.textColorPrimary,
      textColorSecondary: uiConfig.colors.textColorSecondary,
      userBubbleBackground: uiConfig.colors.userBubbleBackground,
      textColorUserBubble: uiConfig.colors.textColorUserBubble,
      botBubbleBackground: uiConfig.colors.botBubbleBackground,
      textColorBotBubble: uiConfig.colors.textColorBotBubble,
      botBubbleBorder: uiConfig.colors.botBubbleBorder,
      systemBubbleBackground: uiConfig.colors.systemBubbleBackground,
      systemBubbleColor: uiConfig.colors.systemBubbleColor,
      systemBubbleBorder: uiConfig.colors.systemBubbleBorder,
      borderColor: uiConfig.colors.borderColor,
      focusRingColor: uiConfig.colors.focusRingColor,
    },
    primaryColor: uiConfig.colors.primary,
    widgetBackground: uiConfig.colors.widgetBackground,
    textColorPrimary: uiConfig.colors.textColorPrimary,
    textColorOnPrimary: uiConfig.colors.textColorOnPrimary,

    markdownSupport: {
      enableUserMarkdown: uiConfig.markdownSupport.enableUserMarkdown,
      enableAssistantMarkdown: uiConfig.markdownSupport.enableAssistantMarkdown,
    },
    errorMessages: {
      default: uiConfig.errorMessages.default,
      unauthorized: uiConfig.errorMessages.unauthorized,
    },

    defaultMinimizedDesktop: uiConfig.defaultMinimizedDesktop,
    defaultMinimizedMobile: uiConfig.defaultMinimizedMobile,
    defaultMinimized: uiConfig.appearance.defaultMinimized ?? uiConfig.defaultMinimizedDesktop ?? false,
    autoOpen: {
      delayDesktop: uiConfig.autoOpen.delayDesktop,
      delayMobile: uiConfig.autoOpen.delayMobile,
      oncePerSession: uiConfig.autoOpen.oncePerSession,
    },
    autoOpenDelay: uiConfig.appearance.autoOpenDelay ?? uiConfig.autoOpen.delayDesktop ?? 0,

    mobileMode: uiConfig.mobileMode,
    mobileHalfHeight: uiConfig.mobileHalfHeight,

    appearance: {
      fontFamily: uiConfig.appearance.fontFamily,
      fontSizeBase: uiConfig.appearance.fontSizeBase,
      widgetShadow: uiConfig.appearance.widgetShadow,
      bubbleShadow: uiConfig.appearance.bubbleShadow,
      widgetMaxWidth: uiConfig.appearance.widgetMaxWidth,
      widgetMaxHeight: uiConfig.appearance.widgetMaxHeight,
      defaultMinimized: uiConfig.appearance.defaultMinimized,
      autoOpenDelay: uiConfig.appearance.autoOpenDelay,
      showCollapsedThinking: uiConfig.appearance.showCollapsedThinking,
      maxMessageLength: uiConfig.appearance.maxMessageLength,
    },
    fontFamily: uiConfig.appearance.fontFamily,
    fontSizeBase: uiConfig.appearance.fontSizeBase,
    widgetShadow: uiConfig.appearance.widgetShadow,
    bubbleShadow: uiConfig.appearance.bubbleShadow,
    widgetMaxWidth: uiConfig.appearance.widgetMaxWidth,
    widgetMaxHeight: uiConfig.appearance.widgetMaxHeight,
    showCollapsedThinking: uiConfig.appearance.showCollapsedThinking,
    maxMessageLength: uiConfig.appearance.maxMessageLength,
  } as WidgetVersionConfig;
};
